import React from 'react';
import { Edit as EditIcon, Medication as MedicationIcon } from '@mui/icons-material';

import type { Medication } from '../types';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

interface PatientMedicationsCardProps {
  medications: Medication[];
  isLoading?: boolean;
  onEdit?: () => void;
}

export const PatientMedicationsCard: React.FC<PatientMedicationsCardProps> = ({
  medications,
  isLoading = false,
  onEdit,
}) => {
  const activeMedications = medications.filter(med => med.is_active !== false);
  const inactiveMedications = medications.filter(med => med.is_active === false);

  return (
    <Card
      sx={{
        width: '100%',
        minWidth: 400,
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 3,
          borderColor: 'borderColors.brand',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <MedicationIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              color: 'textColors.darkText',
              mb: 0.25,
            }}
          >
            Patient Medications
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
            <Typography
              variant="caption"
              sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}
            >
              {medications.length} total • {activeMedications.length} active
            </Typography>
            {onEdit && (
              <Chip
                label="+ Add"
                size="small"
                onClick={onEdit}
                sx={{
                  height: 18,
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              />
            )}
          </Box>
        </Box>
        {onEdit && (
          <Tooltip title="Edit medications">
            <IconButton
              size="small"
              onClick={onEdit}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                },
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent
        sx={{
          p: 1.5,
          pt: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          maxHeight: 400,
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Loading medications...
            </Typography>
          </Box>
        ) : medications.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No medications recorded for this patient.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {/* Active Medications */}
            {activeMedications.length > 0 && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 600,
                    color: 'textColors.lightText',
                    fontSize: '0.625rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Active Medications
                </Typography>
                <Stack spacing={1}>
                  {activeMedications.map(med => (
                    <Box
                      key={med.medication_id}
                      onClick={onEdit}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'backgroundColors.tertiary',
                        border: '1px solid',
                        borderColor: 'borderColors.lightprimary',
                        cursor: onEdit ? 'pointer' : 'default',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': onEdit
                          ? {
                              borderColor: 'primary.main',
                              bgcolor: 'action.hover',
                            }
                          : {},
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: 'textColors.darkText',
                              mb: 0.25,
                            }}
                          >
                            {med.name}
                          </Typography>
                          {med.generic_name && med.generic_name !== med.name && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.6875rem',
                                color: 'textColors.lightText',
                                fontStyle: 'italic',
                              }}
                            >
                              {med.generic_name}
                            </Typography>
                          )}
                          <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            sx={{ mt: 0.5, gap: 0.5 }}
                          >
                            {med.strength && (
                              <Chip
                                label={med.strength}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.625rem',
                                  bgcolor: 'backgroundColors.secondary',
                                }}
                              />
                            )}
                            {med.dosage && (
                              <Chip
                                label={med.dosage}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.625rem',
                                  bgcolor: 'backgroundColors.secondary',
                                }}
                              />
                            )}
                            {med.frequency && (
                              <Chip
                                label={med.frequency}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.625rem',
                                  bgcolor: 'backgroundColors.secondary',
                                }}
                              />
                            )}
                          </Stack>
                          {med.indication && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.6875rem',
                                color: 'textColors.lightText',
                                mt: 0.5,
                                display: 'block',
                              }}
                            >
                              For: {med.indication}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label="Active"
                          size="small"
                          color="success"
                          sx={{ height: 18, fontSize: '0.625rem' }}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Inactive Medications */}
            {inactiveMedications.length > 0 && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 600,
                    color: 'textColors.lightText',
                    fontSize: '0.625rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Inactive Medications
                </Typography>
                <Stack spacing={1}>
                  {inactiveMedications.slice(0, 3).map(med => (
                    <Box
                      key={med.medication_id}
                      onClick={onEdit}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'backgroundColors.tertiary',
                        border: '1px solid',
                        borderColor: 'borderColors.lightprimary',
                        opacity: 0.7,
                        cursor: onEdit ? 'pointer' : 'default',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': onEdit
                          ? {
                              borderColor: 'primary.main',
                              bgcolor: 'action.hover',
                              opacity: 1,
                            }
                          : {},
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        justifyContent="space-between"
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: 'textColors.darkText',
                            }}
                          >
                            {med.name}
                          </Typography>
                          {med.end_date && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.6875rem',
                                color: 'textColors.lightText',
                                mt: 0.25,
                                display: 'block',
                              }}
                            >
                              Ended: {new Date(med.end_date).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label="Inactive"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.625rem',
                            bgcolor: 'backgroundColors.secondary',
                          }}
                        />
                      </Stack>
                    </Box>
                  ))}
                  {inactiveMedications.length > 3 && (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.6875rem', color: 'textColors.lightText', pl: 1 }}
                    >
                      ...and {inactiveMedications.length - 3} more
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
